import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  IconButton,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import {
  MdQrCode2,
  MdClose,
  MdVisibility,
  MdVisibilityOff,
  MdPerson,
  MdVpnKey,
  MdCheckCircle,
} from "react-icons/md";
import { toast } from "react-toastify";
import { getCustomerQrAccess, updateCustomerQrAccess } from "../../utils/api";

const CustomerQrAccessModal = ({ open, setOpen, customer, reload }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [qrCodeEnabled, setQrCodeEnabled] = useState(false);
  const [qrLoginUserName, setQrLoginUserName] = useState("");
  const [qrPassword, setQrPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (open && customer?._id) {
      fetchAccess();
    }
  }, [open, customer?._id]);

  const fetchAccess = async () => {
    setLoading(true);
    const [success, res] = await getCustomerQrAccess(customer._id);
    if (success && res?.customer) {
      setQrCodeEnabled(Boolean(res.customer.qrCodeEnabled));
      setQrLoginUserName(res.customer.qrLoginUserName || res.customer.userName || "");
      setQrPassword(res.customer.qrPassword || "");
    } else {
      setQrCodeEnabled(Boolean(customer?.qrCodeEnabled));
      setQrLoginUserName(customer?.qrLoginUserName || customer?.userName || "");
      setQrPassword(customer?.qrPassword || "");
    }
    setLoading(false);
  };

  const handleSave = async () => {
    if (qrCodeEnabled && !qrLoginUserName.trim()) {
      toast.error("Please enter a QR Username");
      return;
    }
    if (qrCodeEnabled && !qrPassword.trim()) {
      toast.error("Please enter a QR Password");
      return;
    }

    setSaving(true);
    const payload = {
      qrCodeEnabled,
      qrLoginUserName: qrLoginUserName.trim(),
      qrPassword: qrPassword.trim(),
    };

    const [success, res] = await updateCustomerQrAccess(customer._id, payload);
    if (success) {
      toast.success(
        qrCodeEnabled
          ? "QR Portal Access Enabled Successfully!"
          : "QR Portal Access Disabled."
      );
      if (reload) reload();
      setOpen(false);
    } else {
      toast.error(res?.message || "Failed to update QR access");
    }
    setSaving(false);
  };

  const handleClose = () => {
    if (!saving) setOpen(false);
  };

  return (
    <Dialog
      open={Boolean(open)}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: 16,
          padding: 0,
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <div className="bg-slate-900 text-white px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-emerald-500/20 text-emerald-400 rounded-xl border border-emerald-500/30">
            <MdQrCode2 className="text-2xl" />
          </div>
          <div>
            <h2 className="text-base font-bold text-white leading-tight">
              Customer QR Portal Access
            </h2>
            <p className="text-xs text-slate-400 mt-0.5">
              Dedicated access credentials for label printing
            </p>
          </div>
        </div>
        <IconButton
          size="small"
          onClick={handleClose}
          disabled={saving}
          className="text-slate-400 hover:text-white"
        >
          <MdClose />
        </IconButton>
      </div>

      <DialogContent className="p-6 bg-slate-50 flex flex-col gap-5">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-10">
            <CircularProgress size={36} className="text-emerald-600 mb-2" />
            <span className="text-xs text-slate-500">Loading access settings...</span>
          </div>
        ) : (
          <>
            {/* Customer Info Card */}
            <div className="p-3.5 bg-white rounded-xl border border-slate-200 shadow-xs flex items-center justify-between">
              <div>
                <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block">
                  Customer
                </span>
                <span className="text-sm font-black text-slate-800">
                  {customer?.name || customer?.userName || "Customer"}
                </span>
                {customer?.phoneNumber && (
                  <span className="text-xs text-slate-500 block">
                    📞 {customer.phoneNumber}
                  </span>
                )}
              </div>
              <div className="text-right">
                <span
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold ${
                    qrCodeEnabled
                      ? "bg-emerald-100 text-emerald-800 border border-emerald-300"
                      : "bg-slate-100 text-slate-600 border border-slate-300"
                  }`}
                >
                  {qrCodeEnabled ? (
                    <>
                      <MdCheckCircle className="text-emerald-600 text-sm" />
                      Active
                    </>
                  ) : (
                    "Disabled"
                  )}
                </span>
              </div>
            </div>

            {/* Toggle Card */}
            <div className="p-4 bg-white rounded-xl border border-slate-200 shadow-xs flex items-center justify-between">
              <div>
                <span className="text-sm font-bold text-slate-900 block">
                  Enable QR Portal Login
                </span>
                <span className="text-xs text-slate-500">
                  Allows this customer to log in and print sequential QR labels
                </span>
              </div>
              <FormControlLabel
                control={
                  <Switch
                    checked={qrCodeEnabled}
                    onChange={(e) => setQrCodeEnabled(e.target.checked)}
                    color="success"
                  />
                }
                label=""
                className="m-0"
              />
            </div>

            {/* Credentials Fields */}
            {qrCodeEnabled && (
              <div className="p-4 bg-emerald-50/70 rounded-xl border border-emerald-200 flex flex-col gap-4">
                <div className="flex items-center gap-2 text-xs font-bold text-emerald-950">
                  <span>🔐</span> Set Portal Credentials
                </div>

                <TextField
                  label="QR Username / Login ID"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={qrLoginUserName}
                  onChange={(e) => setQrLoginUserName(e.target.value)}
                  placeholder="e.g. bachan or mill_123"
                  className="bg-white rounded"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MdPerson className="text-slate-400" />
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  label="QR Password"
                  type={showPassword ? "text" : "password"}
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={qrPassword}
                  onChange={(e) => setQrPassword(e.target.value)}
                  placeholder="Enter login password"
                  className="bg-white rounded"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MdVpnKey className="text-slate-400" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? (
                            <MdVisibilityOff className="text-slate-500 text-sm" />
                          ) : (
                            <MdVisibility className="text-slate-500 text-sm" />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <div className="bg-white p-3 rounded-lg border border-emerald-200/80 text-[11px] text-emerald-900 leading-relaxed space-y-2">
                  <div className="flex flex-col gap-1.5">
                    <div>
                      💡 <strong>Prabhu Portal Login:</strong>{" "}
                      <code className="bg-emerald-100 px-1.5 py-0.5 rounded font-mono text-emerald-950 font-bold">
                        punjab.prabhulabels.com/login
                      </code>
                    </div>
                    <div className="text-slate-600 text-[10px]">
                      Customer logging in here is redirected directly to their Prabhu QR Portal.
                    </div>
                  </div>

                  <div className="border-t border-emerald-100 pt-2 flex flex-col gap-1.5">
                    <div>
                      🌐 <strong>RiceQRCodes Portal:</strong>{" "}
                      <a
                        href="https://riceqrcodes.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-emerald-100 hover:bg-emerald-200 px-1.5 py-0.5 rounded font-mono text-emerald-950 font-bold underline inline-flex items-center gap-1"
                      >
                        <span>https://riceqrcodes.com</span> ↗
                      </a>
                    </div>
                    <div className="text-slate-600 text-[10px]">
                      Credentials sync automatically to RiceQRCodes.
                    </div>
                    <div className="pt-0.5">
                      <a
                        href="https://riceqrcodes.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-xs shadow-xs"
                      >
                        🚀 Open RiceQRCodes Studio
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions className="p-4 bg-slate-100 border-t border-slate-200 flex justify-between">
        <Button
          onClick={handleClose}
          disabled={saving}
          color="inherit"
          className="font-bold text-xs text-slate-600"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving || loading}
          variant="contained"
          color="success"
          className="font-bold text-xs bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm px-6 py-2"
        >
          {saving ? "Saving..." : "Save QR Access"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomerQrAccessModal;
